import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import type React from "react";
import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { getCurrentUser } from "@/api/services/userService";
import type { AppUser } from "@/types/user";
import { CountryCurrencySetup } from "./components/admin/CountryCurrencySetup";
import { DataSourceConfiguration } from "./components/admin/DataSourceConfiguration";
import { GeneralSettings } from "./components/admin/GeneralSettings";
import { SystemTerminologySettings } from "./components/admin/SystemTerminologySettings";
import { DynamicDashboard } from "./components/dashboard/DynamicDashboard";
import { Header } from "./components/layout/Header";
import { Sidebar } from "./components/layout/Sidebar";
import { SidebarInset, SidebarProvider } from "./components/ui/sidebar";
import { AppProvider, useApp } from "./contexts/AppContext";
import { AppStateProvider } from "./contexts/AppStateContext";
import { BaseForecastProvider } from "./contexts/BaseForecastContext";
import { DataSourceProvider } from "./contexts/DataSourceContext";
import { DrillDownProvider } from "./contexts/DrillDownContext";
import { JurisdictionProvider } from "./contexts/JurisdictionContext";
import { PreferencesProvider } from "./contexts/PreferencesContext";
import { ScenarioProvider } from "./contexts/ScenarioContext";
import {
  UserPreferenceDefaultsProvider,
  useUserPreferenceDefaults,
} from "./contexts/UserPreferenceDefaultsContext";
import {
  UserPreferencesProvider,
  useUserPreferences,
} from "./contexts/UserPreferencesContext";
import { useUserProfile } from "./hooks/useUserProfile";
import { useAuth } from "./providers/AuthProvider";
import { useCookieConsentContext } from "./providers/CookieConsentProvider";
import { IntercomProvider } from "./providers/IntercomProvider";
import { WootricProvider } from "./providers/WootricProvider";
import type { IntercomUser } from "./types/intercom";

// DataSourcePrioritisation is not yet implemented — placeholder component used until built
const DataSourcePrioritisation: React.FC<{
  onNavigate: (page: string) => void;
  highlightSourceType?: string;
}> = () => (
  <div className="p-8 text-center text-gray-500">
    Data Source Prioritisation — coming soon
  </div>
);

import cashforecasterIcon from "@/assets/cashforecaster.svg";
import { getSystemTerminologyConfiguration } from "./api/services/systemTerminologyConfigurationService";
import { AdminDashboardLayout } from "./components/admin/AdminDashboardLayout";
import { ApprovalWorkflowsPage } from "./components/admin/ApprovalWorkflowsPage";
import { RolesPermissionsContainer } from "./components/admin/RolesPermissionsContainer";
import { UserManagement } from "./components/admin/UserManagement";
import { UserPreferenceDefaults } from "./components/admin/UserPreferenceDefaults";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Forecast3Year } from "./components/forecast/Forecast3Year";
import { Forecast12Month } from "./components/forecast/Forecast12Month";
import { ForecastRefreshBanner } from "./components/forecast/ForecastRefreshBanner";
import { ForecastRefreshPage } from "./components/forecast/ForecastRefreshPage";
import { ForecastTimeline } from "./components/forecast/ForecastTimeline";
import { HelpDrawer } from "./components/help/HelpDrawer";
import { LandingPageComingSoon } from "./components/LandingPageComingSoon";
import { LoadingScreen } from "./components/LoadingScreen";
import { OnboardingGate } from "./components/onboarding/OnboardingGate";
import { ProtectedRoute } from "./components/permissions/ProtectedRoute";
import { RecurringTransactionsPage } from "./components/recurring-transactions/RecurringTransactionsPage";
import { ReportsPage } from "./components/reports/ReportsPage";
import { NewScenariosPage } from "./components/scenarios/NewScenariosPage";
import { BankAccountsPage } from "./components/settings/BankAccountsPage";
import { CategoryMappingPage } from "./components/settings/CategoryMappingPage";
import { SettingsPage } from "./components/settings-page";
import { TransactionsPage } from "./components/transactions/TransactionsPage";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Button } from "./components/ui/button";
import { VarianceAnalysisPage } from "./components/variance-analysis-page";
import { ActionsProvider } from "./contexts/ActionsContext";
import { ForecastRefreshProvider } from "./contexts/ForecastRefreshContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { RecommendationsProvider } from "./contexts/RecommendationsContext";
import { UsersProvider } from "./contexts/UsersContext";
import { usePermissions } from "./hooks/usePermissions";
import { useTerminology } from "./hooks/useTerminology";
import {
  mapApiToUiModel,
  updateSystemTerminology,
} from "./lib/terminology-config";
import { ActionsPage } from "./pages/ActionsPage";
import { AIRecommendationsPage } from "./pages/AIRecommendationsPage";
import { ModeProvider, useMode } from "./providers/ModeProvider";
import type { ModeType } from "./types";
import type { Permission } from "./types/permissions";

// Wrapper component to integrate Wootric with user profile from AppContext
const WootricWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user: wootricUser } = useUserProfile();
  const { consent } = useCookieConsentContext();
  const analyticsGranted = consent?.isGranted("analytics") ?? false;
  return (
    <WootricProvider user={wootricUser} enabled={analyticsGranted}>
      {children}
    </WootricProvider>
  );
};

// Wrapper component to integrate Intercom with authenticated user context (live mode only)
const IntercomWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLive } = useMode();
  const { instanceId } = useAuth();
  const { currentUser } = useApp();

  const intercomUser: IntercomUser | null =
    isLive && instanceId && currentUser.email
      ? { email: currentUser.email, instanceId, name: currentUser.name }
      : null;

  return (
    <IntercomProvider user={intercomUser} enabled={isLive}>
      {children}
    </IntercomProvider>
  );
};

// ThemeSync: always-mounted component that applies the current user's saved
// theme whenever the active user changes. Read-only — never writes to context.
// UserPreferencesContext is the single source of truth; settings-page writes
// to it immediately when the theme dropdown changes.
const ThemeSync: React.FC = () => {
  const { currentUser, mode } = useApp();
  const { getUserPreferences } = useUserPreferences();
  const { setTheme } = useTheme();

  useEffect(() => {
    const savedTheme = getUserPreferences(currentUser.id).theme ?? "light";
    setTheme(savedTheme);
    if (mode === "demo") {
      try {
        localStorage.removeItem("cash-forecaster-theme");
      } catch (_) {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.id]);

  return null;
};

// Mapping of page names to required permissions
// Pages not listed here are accessible to all users (e.g. dashboard, settings)
// `undefined` means the page key is valid but needs no permission.
const PAGE_PERMISSIONS: Record<string, Permission | Permission[] | undefined> =
  {
    dashboard: undefined,
    "forecast-90": "view_forecasts",
    "forecast-12m": "view_forecasts",
    "forecast-3y": "view_3year_forecast_screen",
    "forecast-refresh": "trigger_forecast_refresh",
    transactions: "view_transactions",
    "recurring-transactions": "manage_recurring_transactions",
    scenarios: "view_scenarios",
    variance: "view_variance_analysis_screen",
    recommendations: ["view_operational_insights", "view_strategic_insights"],
    reports: ["view_reports", "manage_report_permissions"],
    actions: undefined,
    "admin-users": "manage_users",
    "admin-roles-permissions": "manage_roles_permissions",
    "admin-approval-workflows": "manage_approval_workflows",
    "admin-user-defaults": "manage_user_defaults",
    "admin-dashboard-layout": "manage_dashboard_layout",
    "admin-general-settings": "manage_general_settings",
    "admin-terminology": "manage_terminology",
    "admin-data-sources": "configure_data_sources",
    "admin-category-mapping": "configure_data_sources",
    "admin-country-currency": undefined,
    "bank-accounts": "manage_bank_account_details",
    settings: undefined,
  };

// Deeplinks arrive as a relative app path driven into the iframe by Evo,
// e.g. /admin-data-sources?entities=5
const readDeepLinkFromLocation = (): {
  page: string;
  entityId?: string;
} | null => {
  if (typeof window === "undefined") return null;

  const page = window.location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  if (!page || !(page in PAGE_PERMISSIONS)) return null;

  const entityId =
    new URLSearchParams(window.location.search).get("entities") ?? undefined;

  return { page, entityId };
};

const AppContent: React.FC = () => {
  const { currentUser, setCurrentUser, mode, setMode, setNavigate } = useApp();
  const { isDemoAllowed, isDemo } = useMode();

  const queryClient = useQueryClient();

  // Fetch the real authenticated user in live mode and update AppContext
  const { data: liveUserData } = useQuery({
    queryKey: ["currentUser"],
    queryFn: getCurrentUser,
    enabled: mode === "live",
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  useEffect(() => {
    if (mode !== "live" || !liveUserData) return;
    const mappedUser: AppUser = {
      id: liveUserData.id,
      name: `${liveUserData.firstName} ${liveUserData.lastName ?? ""}`.trim(),
      email: liveUserData.email,
      forecasterRole: liveUserData.role?.name ?? "User",
      systemRole: liveUserData.isSystemAdmin ? "Administrator" : "User",
    };
    setCurrentUser(mappedUser);
  }, [mode, liveUserData, setCurrentUser]);
  const {
    hasPermission,
    hasAnyPermission,
    isSystemAdmin,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = usePermissions();
  const { getUserPreferences } = useUserPreferences();
  const { defaults: roleDefaults } = useUserPreferenceDefaults();

  // Three-layer landing page resolution:
  //   1. User's own My Preferences setting (highest priority — their explicit choice)
  //   2. Role-level default from UserPreferenceDefaults (admin-configurable per role)
  //   3. Fallback to dashboard.
  const resolveInitialPage = (): string => {
    // Layer 1: user's explicit personal preference
    const userPref = getUserPreferences(currentUser.id).defaultLandingPage;
    if (userPref) return userPref;

    // Layer 2: role-level default set by admin
    if (roleDefaults.defaultLandingPage) return roleDefaults.defaultLandingPage;

    // Layer 3: fallback to dashboard
    return "dashboard";
  };

  // Terminology getters — the hook re-renders this component on the
  // `terminology-updated` event so PAGE_TITLES recomputes with fresh values.
  const {
    forecastTerm,
    scenarioTerm,
    varianceTerm,
    actionsTerm,
    getForecastTerm,
    getScenarioTerm,
    getVarianceTerm,
    getActionsTerm,
  } = useTerminology();

  // Bootstrap terminology from backend on app load (live mode only)
  useEffect(() => {
    if (mode !== "live") return;
    let cancelled = false;
    getSystemTerminologyConfiguration()
      .then((apiResponse) => {
        if (cancelled) return;
        const uiModel = mapApiToUiModel(apiResponse);
        updateSystemTerminology(uiModel);
      })
      .catch(() => {
        // Non-critical — fall back to defaults in sessionStorage
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const PAGE_TITLES: Record<string, string> = useMemo(() => {
    const forecasts = `${getForecastTerm(true)}s`;
    return {
      dashboard: "Dashboard",
      "forecast-90": `${forecasts} - 90-Day View`,
      "forecast-12m": `${forecasts} - 12-Month View`,
      "forecast-3y": `${forecasts} - 3-Year View`,
      "forecast-refresh": `${forecasts} - Refresh ${getForecastTerm(true)}`,
      transactions: "Transactions - Synced Transactions",
      "recurring-transactions": "Transactions - Recurring Commitments",
      scenarios: getScenarioTerm(),
      variance: `${getVarianceTerm()} Analysis`,
      recommendations: "AI Recommendations",
      "admin-users": "Users - User Management",
      "admin-roles-permissions": "Users - Roles & Permissions",
      "admin-approval-workflows": "Users - Approval Workflows",
      "admin-user-defaults": "Users - User Defaults",
      "admin-dashboard-layout": "Users - Dashboard Layout",
      "admin-general-settings": "System Settings - General",
      "admin-terminology": "System Settings - Terminology",
      "admin-data-sources": "System Settings - Data Source Config",
      "admin-category-mapping": "System Settings - Parent Child Categories",
      "admin-data-source-prioritisation":
        "System Settings - Data Source Prioritisation",
      "admin-country-currency": "System Settings - Country & Currency",
      "admin-activity-log": "Users - Activity Log",
      reports: "Reports",
      actions: getActionsTerm(),
      "bank-accounts": "Finance Settings - Bank Accounts",
      settings: "My Preferences",
    };
  }, [
    forecastTerm,
    scenarioTerm,
    varianceTerm,
    actionsTerm,
    getForecastTerm,
    getScenarioTerm,
    getVarianceTerm,
    getActionsTerm,
  ]);

  const [currentPage, setCurrentPage] = useState(() => resolveInitialPage());
  const [navigationData, setNavigationData] = useState<
    | {
        highlightSourceType?: string;
        accountFilter?: string;
        initialFilter?: string;
        columnFilters?: Record<string, string>;
        scenarioTab?: string;
        openCreateModal?: boolean;
        scenarioId?: string;
        roleFilter?: string;
        entityId?: string;
      }
    | undefined
  >(undefined);
  const [helpOpen, setHelpOpen] = useState(false);
  const mainScrollRef = useRef<HTMLDivElement>(null);

  // Reset the main content scroll position to the top whenever the page changes
  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, left: 0 });
  }, [currentPage]);

  // Enhanced navigation handler that accepts optional data
  const handleNavigate = (
    page: string,
    data?: {
      highlightSourceType?: string;
      accountFilter?: string;
      initialFilter?: string;
      columnFilters?: Record<string, string>;
      scenarioTab?: string;
      openCreateModal?: boolean;
      scenarioId?: string;
      roleFilter?: string;
      entityId?: string;
    },
  ) => {
    setCurrentPage(page);
    setNavigationData(data);
  };

  // Deeplink resolution — first load only. Applies once permissions are known so
  // an unauthorised target falls back to the normal landing page resolution.
  const deepLinkResolvedRef = useRef(false);
  useEffect(() => {
    if (deepLinkResolvedRef.current || permissionsLoading) return;
    deepLinkResolvedRef.current = true;

    const deepLink = readDeepLinkFromLocation();
    if (!deepLink) return;

    const required = PAGE_PERMISSIONS[deepLink.page];
    const allowed =
      isSystemAdmin ||
      required === undefined ||
      (Array.isArray(required)
        ? hasAnyPermission(...required)
        : hasPermission(required));
    if (!allowed) return;

    setCurrentPage(deepLink.page);
    setNavigationData(
      deepLink.entityId ? { entityId: deepLink.entityId } : undefined,
    );
  }, [permissionsLoading, isSystemAdmin, hasAnyPermission, hasPermission]);

  const clearRoleFilter = useCallback(() => {
    setNavigationData((prev) =>
      prev ? { ...prev, roleFilter: undefined } : prev,
    );
  }, []);

  const clearInitialFilter = useCallback(() => {
    setNavigationData((prev) =>
      prev ? { ...prev, initialFilter: undefined } : prev,
    );
  }, []);

  const clearScenarioNav = useCallback(() => {
    setNavigationData((prev) =>
      prev
        ? {
            ...prev,
            scenarioTab: undefined,
            scenarioId: undefined,
            openCreateModal: undefined,
          }
        : prev,
    );
  }, []);

  // Register handleNavigate globally so OnboardingAssistant (portal) can navigate
  useEffect(() => {
    setNavigate(handleNavigate);
  }, [setNavigate, handleNavigate]);

  const renderDashboard = () => {
    // Always show DynamicDashboard for the Dashboard menu item
    // DynamicDashboard will show welcome message if user has no dashboard cards
    return <DynamicDashboard onNavigate={handleNavigate} />;
  };

  const DEMO_ONLY_PAGES = [
    "forecast-3y",
    "variance",
    "recommendations",
    "reports",
    "settings",
    "admin-user-defaults",
  ];

  const renderPage = () => {
    if (!isDemo && DEMO_ONLY_PAGES.includes(currentPage)) {
      return renderDashboard();
    }

    // Admin routes (no live mode check for admin)
    if (isSystemAdmin && mode === "demo") {
      switch (currentPage) {
        case "dashboard":
          return renderDashboard();
        case "admin-general-settings":
          return (
            <ProtectedRoute permission="manage_general_settings">
              <GeneralSettings />
            </ProtectedRoute>
          );
        case "admin-terminology":
          return (
            <ProtectedRoute permission="manage_terminology">
              <SystemTerminologySettings />
            </ProtectedRoute>
          );
        case "admin-data-sources":
          return (
            <ProtectedRoute permission="configure_data_sources">
              <DataSourceConfiguration onNavigate={handleNavigate} />
            </ProtectedRoute>
          );
        case "admin-data-source-prioritisation":
          return (
            <DataSourcePrioritisation
              onNavigate={handleNavigate}
              highlightSourceType={navigationData?.highlightSourceType}
            />
          );
        case "admin-category-mapping":
          return (
            <ProtectedRoute permission="configure_data_sources">
              <CategoryMappingPage />
            </ProtectedRoute>
          );
        case "bank-accounts":
          return (
            <ProtectedRoute permission="manage_bank_account_details">
              <BankAccountsPage />
            </ProtectedRoute>
          );

        case "admin-country-currency":
          return <CountryCurrencySetup />;
        case "admin-users":
          return (
            <UserManagement
              roleFilter={navigationData?.roleFilter}
              onRoleFilterConsumed={clearRoleFilter}
            />
          );
        case "admin-roles-permissions":
          return <RolesPermissionsContainer />;
        case "admin-approval-workflows":
          return <ApprovalWorkflowsPage />;
        case "admin-user-defaults":
          return <UserPreferenceDefaults />;
        case "admin-dashboard-layout":
          return (
            <ProtectedRoute permission="manage_dashboard_layout">
              <AdminDashboardLayout />
            </ProtectedRoute>
          );
        case "scenarios":
          return (
            <ProtectedRoute permission="view_scenarios">
              <ErrorBoundary>
                <NewScenariosPage
                  initialViewMode={navigationData?.scenarioTab}
                  openCreateModal={navigationData?.openCreateModal}
                  initialScenarioId={navigationData?.scenarioId}
                  onNavConsumed={clearScenarioNav}
                />
              </ErrorBoundary>
            </ProtectedRoute>
          );
        case "reports":
          return (
            <ProtectedRoute
              permission={["view_reports", "manage_report_permissions"]}
            >
              <ReportsPage />
            </ProtectedRoute>
          );
        case "recurring-transactions":
          return (
            <ProtectedRoute permission="manage_recurring_transactions">
              <RecurringTransactionsPage />
            </ProtectedRoute>
          );
        case "actions":
          return <ActionsPage />;
        case "settings":
          return <SettingsPage />;
        default:
          return renderDashboard();
      }
    }

    switch (currentPage) {
      case "dashboard":
        return renderDashboard();
      case "forecast-90":
        return (
          <ProtectedRoute permission="view_forecasts">
            <ForecastTimeline />
          </ProtectedRoute>
        );
      case "forecast-12m":
        return (
          <ProtectedRoute permission="view_forecasts">
            <Forecast12Month />
          </ProtectedRoute>
        );
      case "forecast-3y":
        return (
          <ProtectedRoute permission="view_3year_forecast_screen">
            <Forecast3Year />
          </ProtectedRoute>
        );
      case "forecast-refresh":
        return (
          <ProtectedRoute permission="trigger_forecast_refresh">
            <ForecastRefreshPage />
          </ProtectedRoute>
        );
      case "transactions":
        return (
          <ProtectedRoute permission="view_transactions">
            <ErrorBoundary>
              <TransactionsPage
                accountFilter={navigationData?.accountFilter}
                initialFilter={navigationData?.initialFilter}
                columnFilters={navigationData?.columnFilters}
                onInitialFilterConsumed={clearInitialFilter}
              />
            </ErrorBoundary>
          </ProtectedRoute>
        );
      case "scenarios":
        return (
          <ErrorBoundary>
            <NewScenariosPage
              initialViewMode={navigationData?.scenarioTab}
              openCreateModal={navigationData?.openCreateModal}
              initialScenarioId={navigationData?.scenarioId}
              onNavConsumed={clearScenarioNav}
            />
          </ErrorBoundary>
        );
      case "variance":
        return (
          <ProtectedRoute permission="view_variance_analysis_screen">
            <ErrorBoundary>
              <VarianceAnalysisPage onNavigate={handleNavigate} />
            </ErrorBoundary>
          </ProtectedRoute>
        );
      case "recommendations":
        return (
          <ProtectedRoute
            permission={[
              "view_operational_insights",
              "view_strategic_insights",
            ]}
          >
            <ErrorBoundary>
              <AIRecommendationsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        );
      case "reports":
        return (
          <ProtectedRoute
            permission={["view_reports", "manage_report_permissions"]}
          >
            <ReportsPage />
          </ProtectedRoute>
        );
      case "recurring-transactions":
        return (
          <ProtectedRoute permission="manage_recurring_transactions">
            <ErrorBoundary>
              <RecurringTransactionsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        );
      case "actions":
        return <ActionsPage />;
      // Admin routes - protected by specific permissions
      case "admin-general-settings":
        return (
          <ProtectedRoute permission="manage_general_settings">
            <GeneralSettings />
          </ProtectedRoute>
        );
      case "admin-terminology":
        return (
          <ProtectedRoute permission="manage_terminology">
            <SystemTerminologySettings />
          </ProtectedRoute>
        );
      case "admin-data-sources":
        return (
          <ProtectedRoute permission="configure_data_sources">
            <DataSourceConfiguration onNavigate={handleNavigate} />
          </ProtectedRoute>
        );
      case "admin-category-mapping":
        return (
          <ProtectedRoute permission="configure_data_sources">
            <CategoryMappingPage />
          </ProtectedRoute>
        );
      case "bank-accounts":
        return (
          <ProtectedRoute permission="manage_bank_account_details">
            <BankAccountsPage />
          </ProtectedRoute>
        );

      case "admin-users":
        return (
          <ProtectedRoute permission="manage_users">
            <UserManagement
              roleFilter={navigationData?.roleFilter}
              onRoleFilterConsumed={clearRoleFilter}
            />
          </ProtectedRoute>
        );
      case "admin-roles-permissions":
        return (
          <ProtectedRoute permission="manage_roles_permissions">
            <RolesPermissionsContainer />
          </ProtectedRoute>
        );
      case "admin-approval-workflows":
        return (
          <ProtectedRoute permission="manage_approval_workflows">
            <ApprovalWorkflowsPage />
          </ProtectedRoute>
        );
      case "admin-user-defaults":
        return (
          <ProtectedRoute permission="manage_user_defaults">
            <UserPreferenceDefaults />
          </ProtectedRoute>
        );
      case "admin-dashboard-layout":
        return (
          <ProtectedRoute permission="manage_dashboard_layout">
            <AdminDashboardLayout />
          </ProtectedRoute>
        );

      case "settings":
        return <SettingsPage />;
      default:
        return renderDashboard();
    }
  };

  if (permissionsLoading) return <LoadingScreen />;

  if (permissionsError && mode === "live") {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-6 bg-background p-8">
        <img
          src={cashforecasterIcon}
          alt="Cash Forecaster"
          className="h-16 w-16"
        />
        <div className="max-w-md w-full text-center space-y-4">
          <h2 className="text-xl font-semibold text-foreground">
            Something went wrong
          </h2>
          <p className="text-sm text-muted-foreground">
            {isDemoAllowed
              ? "We couldn't connect to the API. This may be a temporary issue — please try again later, or explore the app using demo data."
              : "We couldn't connect to the API. This may be a temporary issue — please try again later. If this issue persists, please contact your administrator."}
          </p>
          <div className="flex flex-col gap-2 pt-2">
            {isDemoAllowed && (
              <Button variant="default" onClick={() => setMode("demo")}>
                Switch to Demo Mode
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() =>
                queryClient.resetQueries({ queryKey: ["currentUser"] })
              }
            >
              Retry Connection
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-svh bg-background flex flex-col">
      <ThemeSync />
      <SidebarProvider defaultOpen={true} className="flex-1 min-h-0">
        <Sidebar
          currentPage={currentPage}
          onNavigate={handleNavigate}
          data-tour="sidebar"
        />
        <SidebarInset className="flex flex-col min-h-0 overflow-hidden">
          <Header
            pageTitle={PAGE_TITLES[currentPage]}
            currentPage={currentPage}
            onUserChange={(userId) =>
              handleNavigate(
                getUserPreferences(userId).defaultLandingPage ?? "dashboard",
              )
            }
            onHelpOpen={() => setHelpOpen(true)}
          />
          {/* Persistent forecast refresh status banner — appears across all screens when active */}
          <ForecastRefreshBanner />
          <div
            ref={mainScrollRef}
            className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 bg-muted/70 [scrollbar-gutter:stable]"
          >
            {mode === "demo" && currentPage === "dashboard" && (
              <div className="p-4">
                <Alert>
                  <AlertDescription>
                    You're viewing demo data for{" "}
                    <strong>BuildRight Construction Ltd</strong>. Switch to Live
                    Mode to connect your Access Financials account.
                  </AlertDescription>
                </Alert>
              </div>
            )}
            {renderPage()}
          </div>
        </SidebarInset>
      </SidebarProvider>

      {/* Help Drawer */}
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        currentPage={currentPage}
      />

      {/* Onboarding Assistant — portal-rendered above all app chrome */}
      <OnboardingGate />
    </div>
  );
};

function App({
  defaultMode,
  allowDemoMode,
}: {
  defaultMode?: ModeType;
  allowDemoMode?: boolean;
}) {
  return (
    <ModeProvider defaultMode={defaultMode} allowDemoMode={allowDemoMode}>
      <AppProvider>
        <UsersProvider>
          <UserPreferencesProvider>
            <UserPreferenceDefaultsProvider>
              <PreferencesProvider>
                <JurisdictionProvider>
                  <WootricWrapper>
                    <IntercomWrapper>
                      <AppStateProvider>
                        <BaseForecastProvider>
                          <ScenarioProvider>
                            <ForecastRefreshProvider>
                              <ActionsProvider>
                                <RecommendationsProvider>
                                  <DataSourceProvider>
                                    <DrillDownProvider>
                                      <BrowserRouter>
                                        <OnboardingProvider>
                                          <Routes>
                                            <Route
                                              path="/dashboard"
                                              element={<AppContent />}
                                            />
                                            <Route
                                              path="/"
                                              element={
                                                <Navigate
                                                  to="/dashboard"
                                                  replace
                                                />
                                              }
                                            />
                                            {/* Evo drives the iframe to a deeplink path
                                                (e.g. /admin-data-sources) — resolved to a
                                                page by AppContent's deeplink handling. */}
                                            <Route
                                              path="*"
                                              element={<AppContent />}
                                            />
                                          </Routes>
                                        </OnboardingProvider>
                                      </BrowserRouter>
                                    </DrillDownProvider>
                                  </DataSourceProvider>
                                </RecommendationsProvider>
                              </ActionsProvider>
                            </ForecastRefreshProvider>
                          </ScenarioProvider>
                        </BaseForecastProvider>
                      </AppStateProvider>
                    </IntercomWrapper>
                  </WootricWrapper>
                </JurisdictionProvider>
              </PreferencesProvider>
            </UserPreferenceDefaultsProvider>
          </UserPreferencesProvider>
        </UsersProvider>
      </AppProvider>
    </ModeProvider>
  );
}

export default App;
